import React from 'react';
import { View } from 'react-native';

import { Box, Inline } from '@styleseat/ui';
import { BrandColor } from '@styleseat/brand';
import StateLink from '../../StateLink';
import HeaderMenu from './HeaderMenu';
import { Container as SidebarToggle } from '../../shared/SidebarToggle';
import Image from '../../shared/Image';
import Logo from '../../../../images/styleseat-logo-black.svg';
import NavSearchBar from '../NavSearchBar';
import { HeaderProps } from './types';
import { Z_FORCE_BG, Z_NAVIGATION } from '../../ui/utilities/zIndex';
import { MOBILE_HEADER_MAX_HEIGHT, DESKTOP_HEADER_MAX_HEIGHT } from './constants';
import { getLogoSize } from './getLogoSize';
import { HeaderSearchBar } from './HeaderSearchBar';
import { MobileSearch } from './MobileSearch';
import { SetUpMyBusinessLink } from './SetUpMyBusinessLink';
import { ShareTrigger } from './Header.utils';
import { ReferralEntryTrigger } from './ReferralEntryTrigger';

/**
 * Header - wrapping component to display the client header to anonymous and client users.
 *
 * Usage:
 * ```jsx
 * <Header />
 * ```
 *
 * This component takes no props and is connected to the redux store.
 */
export const Header: React.FCWithChildren<HeaderProps> = ({
  providerId,
  headerSearchBarText,
  utmParams = {},
  breakpoints,
  showReferralEntry,
  referralShareProps,
  showSidebarToggle = true,
  showSearch = true,
  showMobileSearch,
  showMobileSearchDot = false,
  showSetUpBusinessLink = false,
  showHeaderSearchBar = false,
  onClickSetUpMyBusiness,
  onMobileSearchClick,
}) => {
  const isMobile = breakpoints.mobile
  || breakpoints.tablet || breakpoints.mobileLayout || breakpoints.desktopNarrow;
  const distribution = { flexGrow: 1, flexBasis: 0 };

  const shouldLogoBeAbsolute = (
    (!showSetUpBusinessLink && providerId)
    || showMobileSearch
    || showReferralEntry
  );

  const logoSize = getLogoSize(breakpoints);

  const referralEntry = React.useMemo(() => (
    <ShareTrigger
      {...referralShareProps}
      renderTrigger={onClick => (
        <ReferralEntryTrigger
          onClick={onClick}
          appearance={isMobile ? 'normal' : 'header'}
        />
      )}
    />
  ), [
    referralShareProps,
    isMobile,
  ]);

  return (
    <Box
      position="relative"
      zIndex={Z_NAVIGATION}
      width="100%"
      alignItems="center"
    >
      <Box
        testID="HeaderUI"
        borderColor={BrandColor.LightGrey}
        borderBottomWidth={1}
        borderStyle="solid"
        borderBottomColor={BrandColor.LightGrey}
        top={0}
        width="100%"
        color={BrandColor.WhiteWash}
        justifyContent="center"
        paddingX={isMobile ? 16 : 48}
      >
        <Inline
          justifyContent="space-between"
          height={isMobile ? MOBILE_HEADER_MAX_HEIGHT : DESKTOP_HEADER_MAX_HEIGHT}
          alignItems="center"
        >
          <Inline
            style={{ ...distribution }}
            justifyContent="flex-start"
            alignItems="center"
          >
            {showSidebarToggle && (
              <Box
                overflow="visible"
                paddingRight={20}
              >
                <SidebarToggle shouldDisplayOnDesktopSize />
              </Box>
            )}
            <View />
            {showSearch && (
              <Inline
                justifyContent="center"
                right={0}
              >
                <NavSearchBar isLandingRedesignEnabled />
              </Inline>
            )}
          </Inline>

          <Box style={{
            ...distribution,
            position: shouldLogoBeAbsolute ? 'absolute' : 'relative',
            left: shouldLogoBeAbsolute ? 0 : undefined,
            right: shouldLogoBeAbsolute ? 0 : undefined,
            flexGrow: isMobile ? 1 : undefined,
            zIndex: Z_FORCE_BG,
          }}
          >
            <Box alignItems="center">
              <StateLink to="home" testID="header-logo" params={utmParams}>
                <Image
                  source={Logo}
                  style={{ ...logoSize as any }}
                />
              </StateLink>
            </Box>

          </Box>

          {(isMobile)
            && (showMobileSearch || showReferralEntry) && (
              <Inline alignItems="center" space={4}>
                {showReferralEntry && !showSetUpBusinessLink && (
                  <Box paddingRight={8}>
                    {referralEntry}
                  </Box>
                )}
                <MobileSearch
                  showMobileSearch={showMobileSearch && !showHeaderSearchBar}
                  showMobileSearchDot={showMobileSearchDot}
                  onMobileSearchClick={onMobileSearchClick}
                />
              </Inline>
          )}

          {showSetUpBusinessLink && (
            (
              <Inline
                alignItems="center"
                paddingRight={isMobile ? 0 : 20}
                justifyContent="flex-end"
                style={{ ...distribution }}
              >
                <SetUpMyBusinessLink
                  breakpoints={breakpoints}
                  onSetUpMyBusinessClicked={onClickSetUpMyBusiness}
                  testID="set-up-my-business-link"
                  shouldUseButtonStyle
                />
              </Inline>
            )
          )}

          <HeaderMenu
            providerId={providerId}
            showReferralEntry={showReferralEntry}
            isLandingRedesignEnabled
            isMobile={isMobile}
          />
        </Inline>

        <HeaderSearchBar
          headerSearchBarText={headerSearchBarText}
          showHeaderSearchBar={showHeaderSearchBar}
        />

      </Box>

    </Box>

  );
};
